import type { ReactElement } from 'react';
import { NavLink } from 'react-router';
import { ADMIN_GNB_SECTIONS } from '@/shared/config';
import { cn } from '@/shared/ui';

interface SidebarNavProps {
  readonly isCollapsed: boolean;
  /** landmark 는 이름이 겹치면 안 된다 — 데스크톱 · 모바일 인스턴스가 서로 다른 라벨을 쓴다. */
  readonly label: string;
  readonly id?: string;
  /** 항목 이동 시 호출 — 모바일 드로어가 자신을 닫는 데 쓴다. */
  readonly onNavigate?: () => void;
}

/** 데스크톱 사이드바와 모바일 드로어가 공유하는 메뉴 본문 — 흑연 바탕 위에 놓인다. */
export const SidebarNav = ({
  isCollapsed,
  label,
  id,
  onNavigate,
}: SidebarNavProps): ReactElement => (
  // 접힘 상태의 sr-only(absolute) 라벨이 nav 의 overflow 에 갇히도록 nav 를 기준 블록(relative)으로 둔다 —
  // 아니면 라벨의 기준 블록이 뷰포트가 되어 문서 높이를 늘리고 셸 밖 문서 전체가 스크롤된다.
  <nav aria-label={label} id={id} className="relative min-h-0 flex-1 overflow-y-auto px-3 py-4">
    <ul className="flex flex-col gap-4">
      {ADMIN_GNB_SECTIONS.map((section) => (
        <li key={section.label}>
          {isCollapsed ? null : (
            <p className="px-3 pb-1.5 text-[11px] font-medium tracking-[0.04em] text-white/40">
              {section.label}
            </p>
          )}

          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.end}
                  onClick={onNavigate}
                  title={isCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      // 흑연 바탕 위 흰 글자 — 선택된 메뉴는 라임 글자와 왼쪽 라임 막대.
                      'relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:outline-none',
                      isActive &&
                        'bg-white/[0.06] font-semibold text-brand-lime before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-brand-lime hover:text-brand-lime',
                      // 아이콘 레일에서는 막대 없이 라임 아이콘·옅은 바탕만 남긴다.
                      isCollapsed && 'justify-center px-0 py-2 before:hidden',
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" aria-hidden="true" />
                  <span className={cn(isCollapsed && 'sr-only')}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  </nav>
);
